import { useState, useEffect, useRef } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  ExpandDrawer,
  Stack,
  List,
  ListItem,
  ListItemButton,
  ListItemDecorator,
  ListItemContent,
  IconButton,
  Badge,
  useBreakpoints,
} from "@financeable-com-au/financeable-ui";
import { useSidebarNavItems } from "@/hooks";
import type { SidebarNavItem } from "@/hooks/useSidebarNavItems/types";

const scrollToSection = (sectionId: string): void => {
  const element = document.getElementById(sectionId.replace("#", ""));
  if (element) {
    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: "smooth" });
  }
};

const SidebarNavigation = () => {
  const { smUp, lgUp } = useBreakpoints();
  const navItems = useSidebarNavItems();
  const [navOpen, setNavOpen] = useState(lgUp);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const isClickScrolling = useRef(false);

  useEffect(() => {
    setNavOpen(lgUp);
  }, [lgUp]);

  // Observe all sectionId targets and track which one is in view.
  useEffect(() => {
    const sectionIds = navItems.flatMap((item) => {
      const ids = [item.sectionId];
      if (item.children) {
        ids.push(...item.children.map((child) => child.sectionId));
      }
      return ids;
    });

    const visibleSections = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleSections.add(entry.target.id);
          } else {
            visibleSections.delete(entry.target.id);
          }
        });

        // Skip observer updates while a click-triggered scroll is in progress.
        if (isClickScrolling.current) return;

        // Find the first parent with any visible element (document order).
        const activeParent = navItems.find((item) => {
          if (visibleSections.has(item.sectionId.replace("#", ""))) return true;
          return item.children?.some((child) => visibleSections.has(child.sectionId.replace("#", "")));
        });

        if (activeParent) {
          // Among visible children of this parent, pick closest to the top of the viewport.
          if (activeParent.children) {
            let closestChildId: string | null = null;
            let closestDistance = Infinity;
            activeParent.children.forEach((child) => {
              const childId = child.sectionId.replace("#", "");
              if (visibleSections.has(childId)) {
                const el = document.getElementById(childId);
                if (el) {
                  const distance = Math.abs(el.getBoundingClientRect().top - 80);
                  if (distance < closestDistance) {
                    closestDistance = distance;
                    closestChildId = childId;
                  }
                }
              }
            });
            if (closestChildId) {
              setActiveSection(closestChildId);
              return;
            }
          }
          setActiveSection(activeParent.sectionId.replace("#", ""));
        }
      },
      { rootMargin: "-80px 0px -50% 0px" },
    );

    const elements: HTMLElement[] = [];
    sectionIds.forEach((id) => {
      const el = document.getElementById(id.replace("#", ""));
      if (el) {
        observer.observe(el);
        elements.push(el);
      }
    });

    return () => {
      elements.forEach((el) => observer.unobserve(el));
      observer.disconnect();
    };
  }, [navItems]);

  const handleNavClick = (sectionId: string) => {
    isClickScrolling.current = true;
    setActiveSection(sectionId.replace("#", ""));
    scrollToSection(sectionId);
    setTimeout(() => {
      isClickScrolling.current = false;
    }, 800);
  };

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !(prev[id] ?? true) }));
  };

  const isItemActive = (item: SidebarNavItem): boolean => {
    if (activeSection === item.sectionId.replace("#", "")) return true;
    return item.children?.some((child) => child.sectionId.replace("#", "") === activeSection) ?? false;
  };

  const renderExpandedItem = (item: SidebarNavItem) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedSections[item.id] ?? hasChildren;
    const parentActive = isItemActive(item);

    if (hasChildren) {
      return (
        <ListItem key={item.id} nested>
          <ListItemButton selected={parentActive} open={isExpanded} onClick={() => toggleSection(item.id)}>
            <ListItemDecorator>{item.icon}</ListItemDecorator>
            <ListItemContent>{item.label}</ListItemContent>
          </ListItemButton>
          <List expandable open={isExpanded}>
            {item.children!.map((child) => (
              <ListItem key={child.id}>
                <ListItemButton
                  selected={activeSection === child.sectionId.replace("#", "")}
                  variant="soft"
                  onClick={() => handleNavClick(child.sectionId)}
                >
                  <ListItemContent>{child.label}</ListItemContent>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </ListItem>
      );
    }

    return (
      <ListItem key={item.id}>
        <ListItemButton selected={parentActive} onClick={() => handleNavClick(item.sectionId)}>
          <ListItemDecorator>{item.icon}</ListItemDecorator>
          <ListItemContent>{item.label}</ListItemContent>
        </ListItemButton>
      </ListItem>
    );
  };

  const renderCollapsedItem = (item: SidebarNavItem) => {
    const parentActive = isItemActive(item);
    const button = (
      <IconButton
        size="xl"
        variant={parentActive ? "primary" : "secondary"}
        intent="neutral"
        onClick={() => handleNavClick(item.sectionId)}
      >
        {item.icon}
      </IconButton>
    );

    if (item.badgeCount !== undefined && item.badgeCount > 0) {
      return (
        <Badge key={item.id} badgeContent={item.badgeCount} size="sm">
          {button}
        </Badge>
      );
    }

    return <span key={item.id}>{button}</span>;
  };

  if (smUp) {
    return (
      <ExpandDrawer open={navOpen} expandedWidth="250px">
        <Stack flexDirection="column" position="sticky" top={64} padding={0} minHeight="100vh">
          {navOpen ? (
            <Stack margin={3} flexDirection="column" alignItems="flex-end">
              <List fullWidth>{navItems.map(renderExpandedItem)}</List>
              {lgUp && (
                <IconButton size="xl" variant="soft" intent="neutral" onClick={() => setNavOpen(false)}>
                  <ChevronLeftIcon />
                </IconButton>
              )}
            </Stack>
          ) : (
            <Stack flexDirection="column" gap={6} alignItems="center" width="100%" marginX="auto" marginY={3}>
              {navItems.map(renderCollapsedItem)}
              {lgUp && (
                <IconButton size="xl" variant="soft" intent="neutral" onClick={() => setNavOpen(true)}>
                  <ChevronRightIcon />
                </IconButton>
              )}
            </Stack>
          )}
        </Stack>
      </ExpandDrawer>
    );
  }
  return null;
};

export { SidebarNavigation };
